import {
  useCallback,
  useRef,
  useState,
  Dispatch,
  SetStateAction,
  MutableRefObject,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import t from 'react-translate';
import RecordRTC from 'recordrtc';

import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import {
  startRolePlaySession,
  StartSessionPayload,
  setAiRolePlaySubmissionClosingReason,
} from 'redux/actions/ai-role-play';
import { wrapThunkAction } from 'redux/utils';
import { danger } from 'styles/global_defaults/colors';
import { RootState } from 'redux/schemas';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';

import { MediaSourceType, requestMediaStream, stopMediaStreams } from 'recording/services/media-stream';
import {
  AIRolePlayStatus,
  COUNTDOWN_DURATION_MS,
  MAX_INACTIVITY_COUNT,
  SessionData,
  VoiceCardState,
  VoiceRecordingState,
} from '../ai-conversation-modal/context';
import streamAIResponse, { ClosingReason, retryTtsStream } from '../ai-conversation-modal/stream-ai-response';
import useTokenRefresh from './use-token-refresh';
import useVoiceRecorder from './use-voice-recorder';
import createAudioPlaybackPipeline from './create-audio-playback-pipeline';

// AbortController cancellations surface as a DOMException/Error with name='AbortError';
// the message varies across browsers, so always check .name.
const isAbortError = (err: unknown): boolean => (err as Error)?.name === 'AbortError';

interface UseVoiceAiConversationHandlersParams {
  audioStreamRef: MutableRefObject<MediaStream | null>
  recorderRef: MutableRefObject<RecordRTC | null>
  aiRolePlayId: number;
  setAiRolePlayStatus: Dispatch<SetStateAction<AIRolePlayStatus>>;
  setShowScenarioDetails: Dispatch<SetStateAction<boolean>>;
  sessionData: SessionData;
  setSessionData: Dispatch<SetStateAction<SessionData>>;
  setIsTimeout: Dispatch<SetStateAction<boolean>>;
  abortControllerRef: MutableRefObject<AbortController | null>;
  setIsRolePlayStarting: Dispatch<SetStateAction<boolean>>;
  setMicrophoneEnabled: Dispatch<SetStateAction<boolean>>;
  setOpeningNarrative: Dispatch<SetStateAction<string>>;
  setVoiceRecordingState: Dispatch<SetStateAction<VoiceRecordingState | null>>;
  resetConversation: () => void;
  setInactivityCount: Dispatch<SetStateAction<number>>;
  inactivityCount: number;
}

interface UseVoiceAiConversationHandlersReturn {
  handleStartVoiceConversation: () => void;
  aiVoiceCardState: VoiceCardState;
  learnerVoiceCardState: VoiceCardState;
  aiMediaStream: MediaStream | null;
  learnerMediaStream: MediaStream | null;
  isStreamError: boolean;
  handleRestartFromStreamError: () => void;
}

const useVoiceAiConversationHandlers = ({
  setAiRolePlayStatus,
  setShowScenarioDetails,
  sessionData,
  setSessionData,
  setIsTimeout,
  abortControllerRef,
  setIsRolePlayStarting,
  setMicrophoneEnabled,
  audioStreamRef,
  recorderRef,
  aiRolePlayId,
  setOpeningNarrative,
  setVoiceRecordingState,
  resetConversation,
  setInactivityCount,
  inactivityCount,
}: UseVoiceAiConversationHandlersParams): UseVoiceAiConversationHandlersReturn => {
  const dispatch = useDispatch();
  const currentCatalogId = useSelector((state: RootState) => state.app.currentCatalogId);

  const [aiVoiceCardState, setAiVoiceCardState] = useState(VoiceCardState.IDLE);
  const [learnerVoiceCardState, setLearnerVoiceCardState] = useState(VoiceCardState.IDLE);
  const [aiMediaStream, setAiMediaStream] = useState<MediaStream | null>(null);
  const [learnerMediaStream, setLearnerMediaStream] = useState<MediaStream | null>(null);
  const [isStreamError, setIsStreamError] = useState(false);

  // Holds the resolve function of the restart-wait Promise; called when user clicks Restart
  const restartResolverRef = useRef<(() => void) | null>(null);
  const hasReceivedNarrativeRef = useRef(false);
  // Monotonically increasing counter — each handleStartVoiceConversation invocation captures
  // its own value so the finally block can detect whether a newer session has taken over.
  const sessionIdRef = useRef(0);

  // Session data ref avoids stale closures across async cycles
  const sessionDataRef = useRef<SessionData>(sessionData);
  sessionDataRef.current = sessionData;

  const submissionIdRef = useRef<number | null>(sessionData.submissionId);
  submissionIdRef.current = sessionData.submissionId;

  const inactivityCountRef = useRef(inactivityCount);
  inactivityCountRef.current = inactivityCount;

  const {
    recordLearnerTurn,
    setMicActive,
    initializeAnalyser,
    cleanupRecorder,
  } = useVoiceRecorder({ audioStreamRef, recorderRef, setInactivityCount });

  const { ensureFreshToken } = useTokenRefresh({ sessionData, setSessionData });

  // Ref allows onConfirm (inside the callback) to call handleStartVoiceConversation
  // without creating a circular useCallback dependency.
  const handleStartVoiceConversationRef = useRef<() => void>(() => {});

  const handleStartVoiceConversation = useCallback(async () => {
    // Abort any in-flight session so its while loop exits before this one starts.
    // Without this, Start Over leaves the old session running concurrently.
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Capture this session's ID before any await so the finally block can detect
    // whether a newer session has taken over (immune to audioStreamRef timing gaps).
    sessionIdRef.current += 1;
    const thisSessionId = sessionIdRef.current;

    hasReceivedNarrativeRef.current = false;
    setInactivityCount(0);
    setOpeningNarrative('');
    setAiRolePlayStatus(AIRolePlayStatus.IN_PROGRESS);
    setIsRolePlayStarting(true);
    setShowScenarioDetails(false);
    // Reset so a later setVoiceRecordingState(COUNTDOWN) isn't a same-value no-op.
    setVoiceRecordingState(VoiceRecordingState.IDLE);

    let stream: MediaStream | null = null;

    try {
      // 1. Request microphone access
      stream = await requestMediaStream(MediaSourceType.MIC);
      audioStreamRef.current = stream;
      setLearnerMediaStream(stream);
      setMicrophoneEnabled(true);

      // Set up AnalyserNode for silence detection (reused across turns)
      initializeAnalyser(stream);
      // Permission acquired; keep mic muted until it's the learner's turn
      setMicActive(false);

      // 2. Start role-play session to get JWT and endpoint
      const sessionRes = await wrapThunkAction(
        dispatch(startRolePlaySession({
          catalogId: currentCatalogId,
          aiRolePlayId,
        })),
      );

      const sessionPayload = sessionRes.payload as StartSessionPayload;
      if (!sessionPayload?.jwtToken || !sessionPayload?.aiApiEndpoint) {
        throw new Error('Invalid session payload');
      }

      const normalizedEndpoint = sessionPayload.aiApiEndpoint.replace(/\/+$/, '');
      const initialSessionData: SessionData = {
        aiApiEndpoint: normalizedEndpoint,
        jwtToken: sessionPayload.jwtToken,
        submissionId: sessionPayload.submissionId || null,
        expiresAt: sessionPayload.expiresAt || null,
        languageLocale: sessionPayload.languageLocale,
      };

      setSessionData(initialSessionData);
      sessionDataRef.current = initialSessionData;
      submissionIdRef.current = sessionPayload.submissionId || null;

      setIsTimeout(false);

      // One session-scoped controller — never replaced during the session so unmount's abort()
      // always propagates. The local `signal` is captured here; the loop reads it directly rather
      // than going through the ref, so ref-nulling in unmount cleanup can't hide the abort.
      const sessionController = new AbortController();
      abortControllerRef.current = sessionController;
      const { signal } = sessionController;

      // 3. Run the voice conversation loop (iterative — avoids unbounded call-stack growth)
      // AI persona always speaks first, so the first turn carries no user audio.
      let isFirstTurn = true;
      let nextAudio: string | null = null;

      while (!signal.aborted) {
        const isOpeningTurn = isFirstTurn;
        // Refresh token before each API call
        const token = await ensureFreshToken(); // eslint-disable-line no-await-in-loop

        let hasFirstAudioDelta = false;
        let conversationClosingReason: ClosingReason | null = null;
        let hadStreamError = false;
        // True when the error occurred after audio had already started streaming (connection drop
        // or AI service error mid-audio) — triggers retry-tts instead of re-recording.
        let hadAudioStreamingError = false;

        const {
          appendChunk,
          finishStreaming: finishAudioStreaming,
          discardSentence,
          playbackDone,
          cleanup: cleanupTurnAudio,
        } = createAudioPlaybackPipeline(setAiMediaStream, signal);

        setAiVoiceCardState(VoiceCardState.THINKING);
        setLearnerVoiceCardState(VoiceCardState.IDLE);

        let bufferedFirstTurnAudioChunks: Array<{ bytes: Uint8Array; sentenceId: number }> = [];
        let isFirstTurnAudioDoneWhileIntroClosed = false;
        let hasStartedFirstTurnCountdown = false;
        let isFirstTurnIntroOpen = !isOpeningTurn;

        const appendTurnAudioChunk = (bytes: Uint8Array, sentenceId: number) => {
          appendChunk(bytes, sentenceId);
          if (!hasFirstAudioDelta) {
            hasFirstAudioDelta = true;
            setAiVoiceCardState(VoiceCardState.SPEAKING);
          }
        };

        const flushBufferedFirstTurnAudio = () => {
          bufferedFirstTurnAudioChunks.forEach(({ bytes, sentenceId }) => appendTurnAudioChunk(bytes, sentenceId));
          bufferedFirstTurnAudioChunks = [];
        };

        const openFirstTurnIntro = () => {
          if (isFirstTurnIntroOpen || signal.aborted) return;

          isFirstTurnIntroOpen = true;
          setVoiceRecordingState(VoiceRecordingState.RECORDING);
          flushBufferedFirstTurnAudio();

          if (isFirstTurnAudioDoneWhileIntroClosed) {
            finishAudioStreaming();
          }
        };

        const startFirstTurnCountdown = () => {
          if (!isOpeningTurn || hasStartedFirstTurnCountdown || signal.aborted) return;

          hasStartedFirstTurnCountdown = true;
          setIsRolePlayStarting(false);
          setVoiceRecordingState(VoiceRecordingState.COUNTDOWN);

          const timer = setTimeout(openFirstTurnIntro, COUNTDOWN_DURATION_MS);
          signal.addEventListener('abort', () => clearTimeout(timer), { once: true });
        };

        await streamAIResponse({ // eslint-disable-line no-await-in-loop
          aiApiEndpoint: sessionDataRef.current.aiApiEndpoint,
          token,
          // First turn: no learner audio yet; subsequent turns: send audio or null (silence)
          ...(isOpeningTurn ? { userMessage: null } : { userAudio: nextAudio }),
          onChunk: (chunk) => {
            // Ignore chunks that resolve after this session's abort() fired.
            if (signal.aborted) return;
            if (!hasReceivedNarrativeRef.current) {
              setOpeningNarrative((prev) => prev + chunk.content);
              if (chunk.isLastChunk) {
                hasReceivedNarrativeRef.current = true;
                startFirstTurnCountdown();
              }
            }
            if (chunk.closingReason === ClosingReason.COMPLETED
                || chunk.closingReason === ClosingReason.TERMINATED) {
              conversationClosingReason = chunk.closingReason;
              // All state updates (Redux + React) deferred until audio playback completes below
            }
          },
          onAudioDelta: ({ audio, sentenceId }) => {
            // Same as onChunk — ignore late chunks after abort.
            if (signal.aborted) return;
            const bytes = Uint8Array.from(atob(audio), (c) => c.charCodeAt(0));
            if (isOpeningTurn && !isFirstTurnIntroOpen) {
              bufferedFirstTurnAudioChunks.push({ bytes, sentenceId });
              return;
            }
            appendTurnAudioChunk(bytes, sentenceId);
          },
          onAudioDone: () => {
            if (signal.aborted) return;
            if (isOpeningTurn && !isFirstTurnIntroOpen) {
              isFirstTurnAudioDoneWhileIntroClosed = true;
              return;
            }
            finishAudioStreaming();
          },
          onAudioError: ({ sentenceId }) => {
            // Not fatal — drop this sentence's audio (wherever it's currently buffered) and keep streaming
            if (signal.aborted) return;
            bufferedFirstTurnAudioChunks = bufferedFirstTurnAudioChunks.filter((chunk) => chunk.sentenceId !== sentenceId);
            discardSentence(sentenceId);
          },
          onError: (err) => {
            if (!isAbortError(err)) {
              hadStreamError = true;
              hadAudioStreamingError = hasFirstAudioDelta;
              cleanupTurnAudio();
              dispatch(addAlertMessage({
                type: AlertMessageType.WARNING,
                header: t.FORM.OOPS(),
                message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SOMETHING_WENT_WRONG(),
                customColor: danger,
              }));
            }
          },
          abortSignal: signal,
        }).catch((err) => {
          if (!isAbortError(err)) {
            hadStreamError = true;
            hadAudioStreamingError = hasFirstAudioDelta;
            cleanupTurnAudio();
          }
          setAiVoiceCardState(VoiceCardState.IDLE);
          setLearnerVoiceCardState(VoiceCardState.IDLE);
        });

        if (signal.aborted) {
          // Only place left to close this turn's AudioContext on abort mid-stream.
          cleanupTurnAudio();
          setAiVoiceCardState(VoiceCardState.IDLE);
          setLearnerVoiceCardState(VoiceCardState.IDLE);
          break;
        }

        if (hadStreamError) {
          setAiVoiceCardState(VoiceCardState.IDLE);
          setLearnerVoiceCardState(VoiceCardState.IDLE);
          setMicActive(false);

          if (isOpeningTurn && !isFirstTurnIntroOpen) {
            resetConversation();
            break;
          }

          if (isOpeningTurn && !hadAudioStreamingError) {
            // First turn failed before any audio arrived — reset back to the start screen
            resetConversation();
            break;
          }

          // Surface a Restart button and pause the loop until the user clicks it
          setIsStreamError(true);
          await new Promise<void>((resolve) => { // eslint-disable-line no-await-in-loop
            const onAbort = () => {
              restartResolverRef.current = null;
              resolve();
            };
            restartResolverRef.current = () => {
              signal.removeEventListener('abort', onAbort);
              resolve();
            };
            signal.addEventListener('abort', onAbort, { once: true });
          });
          setIsStreamError(false);

          if (signal.aborted) break;

          if (hadAudioStreamingError) {
            // Audio started but the stream broke mid-delivery — keep retrying via retry-tts,
            // showing the restart button again on each failure, until audio plays or session ends.
            let retrySucceeded = false;
            while (!signal.aborted && !retrySucceeded) { // eslint-disable-line no-await-in-loop
              const retryToken = await ensureFreshToken(); // eslint-disable-line no-await-in-loop

              if (signal.aborted) break;

              const {
                appendChunk: retryAppendChunk,
                finishStreaming: retryFinishStreaming,
                discardSentence: retryDiscardSentence,
                playbackDone: retryPlaybackDone,
                cleanup: retryCleanup,
              } = createAudioPlaybackPipeline(setAiMediaStream, signal);

              setAiVoiceCardState(VoiceCardState.THINKING);
              setLearnerVoiceCardState(VoiceCardState.IDLE);

              let hasFirstRetryAudioDelta = false;
              let retryHadError = false;

              await retryTtsStream({ // eslint-disable-line no-await-in-loop
                aiApiEndpoint: sessionDataRef.current.aiApiEndpoint,
                token: retryToken,
                onAudioDelta: ({ audio, sentenceId }) => {
                  if (signal.aborted) return;
                  const bytes = Uint8Array.from(atob(audio), (c) => c.charCodeAt(0));
                  retryAppendChunk(bytes, sentenceId);
                  if (!hasFirstRetryAudioDelta) {
                    hasFirstRetryAudioDelta = true;
                    setAiVoiceCardState(VoiceCardState.SPEAKING);
                  }
                },
                onAudioDone: () => {
                  if (signal.aborted) return;
                  retryFinishStreaming();
                },
                onAudioError: ({ sentenceId }) => {
                  if (signal.aborted) return;
                  retryDiscardSentence(sentenceId);
                },
                onError: (err) => {
                  if (!isAbortError(err)) {
                    retryHadError = true;
                    retryCleanup();
                    dispatch(addAlertMessage({
                      type: AlertMessageType.WARNING,
                      header: t.FORM.OOPS(),
                      message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SOMETHING_WENT_WRONG(),
                      customColor: danger,
                    }));
                  }
                },
                abortSignal: signal,
              }).catch((err) => {
                if (!isAbortError(err)) {
                  retryHadError = true;
                  retryCleanup();
                }
                setAiVoiceCardState(VoiceCardState.IDLE);
                setLearnerVoiceCardState(VoiceCardState.IDLE);
              });

              if (signal.aborted) {
                // Same as the main loop's abort branch — close this retry's AudioContext.
                retryCleanup();
                break;
              }

              // audio_done arrived with no audio_delta — empty TTS response.
              // Treat as a failure so the user sees the restart button rather
              // than silently advancing to their speaking turn with no AI audio.
              if (!retryHadError && !hasFirstRetryAudioDelta) {
                retryHadError = true;
                retryCleanup();
                dispatch(addAlertMessage({
                  type: AlertMessageType.WARNING,
                  header: t.FORM.OOPS(),
                  message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SOMETHING_WENT_WRONG(),
                  customColor: danger,
                }));
              }

              if (retryHadError) {
                setAiVoiceCardState(VoiceCardState.IDLE);
                setLearnerVoiceCardState(VoiceCardState.IDLE);
                setIsStreamError(true);
                await new Promise<void>((resolve) => { // eslint-disable-line no-await-in-loop
                  const onAbort = () => {
                    restartResolverRef.current = null;
                    resolve();
                  };
                  restartResolverRef.current = () => {
                    signal.removeEventListener('abort', onAbort);
                    resolve();
                  };
                  signal.addEventListener('abort', onAbort, { once: true });
                });
                setIsStreamError(false);
                // eslint-disable-next-line no-continue
                continue;
              }

              await retryPlaybackDone; // eslint-disable-line no-await-in-loop
              retryCleanup();
              setAiVoiceCardState(VoiceCardState.IDLE);
              retrySucceeded = true;
            }

            if (signal.aborted) break;
          }

          // Resume from the learner's turn so the conversation can continue
          if (isOpeningTurn) {
            setOpeningNarrative('');
          }
          setLearnerVoiceCardState(VoiceCardState.SPEAKING);
          setMicActive(true);
          nextAudio = await recordLearnerTurn(signal); // eslint-disable-line no-await-in-loop
          isFirstTurn = false;
          setMicActive(false);
          setLearnerVoiceCardState(VoiceCardState.IDLE);

          if (signal.aborted) break;
          // eslint-disable-next-line no-continue
          continue;
        }

        // Always wait for audio playback to finish before updating UI state or
        // moving to the next turn — event: done arrives while audio is still playing.
        await playbackDone; // eslint-disable-line no-await-in-loop
        cleanupTurnAudio();

        setAiVoiceCardState(VoiceCardState.IDLE);

        // The opening narrative caption is only needed while the AI speaks its first
        // line — hide it once that line has finished playing.
        if (isOpeningTurn) {
          setOpeningNarrative('');
        }

        if (signal.aborted) break;

        if (inactivityCountRef.current === MAX_INACTIVITY_COUNT) {
          dispatch(addAlertMessage({
            type: AlertMessageType.WARNING,
            header: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SESSION_ENDED(),
            message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SESSION_ENDED_DUE_TO_INACTIVITY(),
            customColor: danger,
          }));
          setVoiceRecordingState(VoiceRecordingState.IDLE);
          setAiRolePlayStatus(AIRolePlayStatus.FAILED);
          break;
        }

        // Conversation ended — update all state only after audio has finished playing.
        if (conversationClosingReason) {
          if (submissionIdRef.current) {
            dispatch(setAiRolePlaySubmissionClosingReason({
              aiRolePlaySubmissionId: submissionIdRef.current,
              closingReason: conversationClosingReason,
              aiRolePlayId,
            }));
          }
          if (conversationClosingReason === ClosingReason.TERMINATED) {
            dispatch(addAlertMessage({
              type: AlertMessageType.WARNING,
              header: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SESSION_ENDED(),
              message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.ROLE_PLAY_ENDED_DUE_TO_RESPONSE(),
              customColor: danger,
            }));
          }
          setVoiceRecordingState(VoiceRecordingState.COMPLETED);
          const completionTimer = setTimeout(() => {
            setVoiceRecordingState(VoiceRecordingState.IDLE);
            setAiRolePlayStatus(AIRolePlayStatus.COMPLETED);
          }, 2000);
          signal.addEventListener('abort', () => clearTimeout(completionTimer), { once: true });
          break;
        }

        // Learner's turn — enable mic only for recording, then mute again
        setLearnerVoiceCardState(VoiceCardState.SPEAKING);
        setMicActive(true);

        nextAudio = await recordLearnerTurn(signal); // eslint-disable-line no-await-in-loop
        isFirstTurn = false;

        setMicActive(false);
        setLearnerVoiceCardState(VoiceCardState.IDLE);

        if (signal.aborted) break;
      }
    } catch (error) {
      if ((error as Error)?.name === 'NotAllowedError' || (error as Error)?.name === 'PermissionDeniedError') {
        setMicrophoneEnabled(false);

        dispatch(openConfirmationDialog({
          title: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.MICROPHONE_DISABLED_OVERLAY.TITLE(),
          bodyText: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.MICROPHONE_DISABLED_OVERLAY.DESCRIPTION(),
          confirmText: t.FORM.TRY_AGAIN(),
          cancelText: t.FORM.BACK(),
          onCancel: resetConversation,
          onConfirm: handleStartVoiceConversationRef.current,
        }));
      } else if (!isAbortError(error)) {
        dispatch(addAlertMessage({
          type: AlertMessageType.WARNING,
          header: t.FORM.OOPS(),
          message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SOMETHING_WENT_WRONG(),
          customColor: danger,
        }));
      }
      setAiVoiceCardState(VoiceCardState.IDLE);
      setLearnerVoiceCardState(VoiceCardState.IDLE);
      setVoiceRecordingState(VoiceRecordingState.IDLE);
      // Skip state resets on abort — either resetConversation already set them
      // (exit flow) or a new session is already running (Start Over) and its own
      // state must not be overwritten.
      if (!isAbortError(error)) {
        setAiRolePlayStatus(AIRolePlayStatus.NOT_STARTED);
        setShowScenarioDetails(true);
      }
    } finally {
      // Always release all audio resources regardless of how the session ended.
      // setMicActive(false) must be here — if recordLearnerTurn throws (abort or
      // null-stream), control jumps directly to catch/finally and skips the
      // inline setMicActive(false) calls after each await.
      setMicActive(false);
      setIsStreamError(false);
      restartResolverRef.current = null;
      cleanupRecorder();
      // Stop our own stream without touching the ref or shared UI state if a
      // newer session has already taken over. The session ID check is immune to
      // the audioStreamRef timing gap (new session sets it only after getUserMedia
      // resolves, which is slower than the microtask that runs this finally block).
      const isCurrentSession = sessionIdRef.current === thisSessionId;
      if (stream) {
        stopMediaStreams(stream);
        if (isCurrentSession) {
          audioStreamRef.current = null;
        }
      }
      if (recorderRef.current) {
        recorderRef.current.destroy();
        recorderRef.current = null;
      }
      if (isCurrentSession) {
        setAiMediaStream(null);
        setLearnerMediaStream(null);
        setIsRolePlayStarting(false);
      }
    }
  }, [
    dispatch,
    currentCatalogId,
    aiRolePlayId,
    setAiRolePlayStatus,
    setShowScenarioDetails,
    setSessionData,
    setIsTimeout,
    setIsRolePlayStarting,
    setMicrophoneEnabled,
    audioStreamRef,
    recorderRef,
    abortControllerRef,
    ensureFreshToken,
    recordLearnerTurn,
    setMicActive,
    initializeAnalyser,
    cleanupRecorder,
    setOpeningNarrative,
    setVoiceRecordingState,
    resetConversation,
    setInactivityCount,
  ]);

  handleStartVoiceConversationRef.current = handleStartVoiceConversation;

  const handleRestartFromStreamError = useCallback(() => {
    if (restartResolverRef.current) {
      restartResolverRef.current();
      restartResolverRef.current = null;
    }
  }, []);

  return {
    handleStartVoiceConversation,
    aiVoiceCardState,
    learnerVoiceCardState,
    aiMediaStream,
    learnerMediaStream,
    isStreamError,
    handleRestartFromStreamError,
  };
};

export default useVoiceAiConversationHandlers;
